import { useCallback, useEffect, useRef, useState } from "react";
import { setFormDirty } from "./dirty-forms";
type UseFormSafetyOptions = {
  isDirty: boolean;
  onDiscard?: () => void;
  enableNavigationGuard?: boolean;
};

type DiscardDialogState = {
  isOpen: boolean;
  onDiscard: () => void;
  onContinue: () => void;
};

export function useFormSafety({
  isDirty,
  onDiscard,
  enableNavigationGuard = true,
}: UseFormSafetyOptions) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);

  // Lets an access change that would close this page ask before it does.
  useEffect(() => {
    if (!isDirty) return;
    const id = Symbol("dirty-form");
    setFormDirty(id, true);
    return () => setFormDirty(id, false);
  }, [isDirty]);

  const allowNextPopRef = useRef(false);
  const guardedUrlRef = useRef<string | null>(null);

  const promptDiscard = useCallback(
    (action?: () => void) => {
      if (!isDirty) {
        action?.();
        return;
      }
      pendingActionRef.current = action ?? null;
      setIsDialogOpen(true);
    },
    [isDirty]
  );

  const handleBeforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      if (!isDirty) {
        return;
      }
      event.preventDefault();
      event.returnValue = "";
      return "";
    },
    [isDirty]
  );

  useEffect(() => {
    if (!enableNavigationGuard || !isDirty || typeof window === "undefined") {
      return;
    }
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [enableNavigationGuard, handleBeforeUnload, isDirty]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    if (isDirty) {
      if (!guardedUrlRef.current) {
        guardedUrlRef.current = window.location.href;
      }
      return;
    }
    guardedUrlRef.current = window.location.href;
  }, [isDirty]);

  useEffect(() => {
    if (!enableNavigationGuard || !isDirty || typeof window === "undefined") {
      return;
    }

    const handlePopState = () => {
      if (allowNextPopRef.current) {
        allowNextPopRef.current = false;
        return;
      }
      const guardedUrl = guardedUrlRef.current ?? window.location.href;
      window.history.pushState(null, "", guardedUrl);
      promptDiscard(() => {
        allowNextPopRef.current = true;
        window.history.back();
      });
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [enableNavigationGuard, isDirty, promptDiscard]);

  const handleDiscardConfirmed = useCallback(() => {
    onDiscard?.();
    pendingActionRef.current?.();
    pendingActionRef.current = null;
    setIsDialogOpen(false);
  }, [onDiscard]);

  const handleDiscardCancelled = useCallback(() => {
    pendingActionRef.current = null;
    setIsDialogOpen(false);
  }, []);

  return {
    promptDiscard,
    discardDialogState: {
      isOpen: isDialogOpen,
      onDiscard: handleDiscardConfirmed,
      onContinue: handleDiscardCancelled,
    } as DiscardDialogState,
  };
}
